import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { INTERNAL_ROLES, InternalRole, isRoleAllowedForPath } from '../../config/role-routes';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: InternalRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!INTERNAL_ROLES.includes(user.role as InternalRole)) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role as InternalRole)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-3">🔒</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Acceso denegado</h2>
          <p className="text-sm text-slate-600">
            Tu rol ({user.role}) no tiene permisos para esta sección.
          </p>
        </div>
      </div>
    );
  }

  if (!isRoleAllowedForPath(user.role, location.pathname)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-3">🔒</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Acceso denegado</h2>
          <p className="text-sm text-slate-600">
            Tu rol no tiene acceso a esta ruta.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}